"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;
var _ark = require("@dateam/ark");
var _arkReact = require("@dateam/ark-react");
var _propTypes = _interopRequireDefault(require("prop-types"));
var _react = _interopRequireDefault(require("react"));
var _FormContext = _interopRequireDefault(require("./FormContext"));
const availableCase = ['lowercase', 'uppercase'];
const FormInput = _ref => {
  let {
    type,
    value,
    placeholder,
    autocomplete = 'off',
    spellcheck,
    autocapitalize,
    forceCase,
    className,
    multiline,
    rows,
    maxlength,
    readOnly,
    required,
    icon,
    state,
    min,
    max,
    regex,
    managed,
    inputMode,
    pattern,
    onChange,
    onFocus,
    onBlur,
    onPaste
  } = _ref;
  const {
    controlId,
    isFocus,
    required: inputIsRequired,
    onBlur: onInputBlur,
    onFocus: onInputFocus,
    onRequiredChange,
    setState
  } = _react.default.useContext(_FormContext.default);
  const internalValueOptions = _react.default.useMemo(() => ({
    locked: managed !== true && isFocus
  }), [isFocus, managed]);
  const [internalValue, setInternalValue] = (0, _arkReact.useInternalValueManager)(value, internalValueOptions);
  const [isInvalid, setIsInvalid] = _react.default.useState(false);
  _react.default.useEffect(() => setState?.(state), [state]);
  _react.default.useEffect(() => onRequiredChange?.(required ?? false), [required, onRequiredChange]);
  const checkInputValidation = _react.default.useCallback(value => {
    if ((0, _ark.isNumber)(maxlength) && value.length > maxlength) return;
    let inputIsInvalid = false;
    if (inputIsRequired && value.length === 0) {
      inputIsInvalid = true;
    }
    if (forceCase != null && availableCase.includes(forceCase) && (0, _ark.isString)(value)) {
      if (forceCase === 'uppercase') value = value.toUpperCase();else if (forceCase === 'lowercase') value = value.toLowerCase();
    }
    if ((0, _ark.isString)(regex) && inputIsInvalid === false) {
      const regexpr = new RegExp(regex);
      inputIsInvalid = !regexpr.test(value);
    }
    return inputIsInvalid === false;
  }, [maxlength, inputIsRequired]);
  _react.default.useEffect(() => {
    if (!(0, _ark.isDefined)(value)) return;
    const isValid = checkInputValidation(value);
    setIsInvalid(isValid === false);
  }, [value, checkInputValidation, setIsInvalid]);
  const onInputChange = _react.default.useCallback(event => {
    const {
      value: inputValue
    } = event.target;
    const isValid = checkInputValidation(inputValue);
    setInternalValue(inputValue);
    setIsInvalid(isValid === false);
    onChange?.(inputValue, event);
  }, [onChange, setInternalValue, setIsInvalid]);
  const textLength = _react.default.useMemo(() => {
    return (0, _ark.isString)(internalValue) ? internalValue.length : 0;
  }, [internalValue]);
  const inputType = _react.default.useMemo(() => type ?? 'text', [type]);
  const Component = _react.default.useMemo(() => {
    return multiline === true ? 'textarea' : 'input';
  }, [multiline]);
  const showLengthIndicator = _react.default.useMemo(() => isFocus && readOnly !== true && (0, _ark.isNumber)(maxlength), [readOnly, isFocus, maxlength]);
  const handleInputBlur = _react.default.useCallback(event => {
    onInputBlur?.();
    onBlur?.(internalValue ?? '', event);
  }, [onInputBlur, onBlur, internalValue]);
  const handleInputFocus = _react.default.useCallback(event => {
    onInputFocus?.();
    onFocus?.(event);
  }, [onInputFocus, onFocus]);
  const handleInputPaste = _react.default.useCallback(event => {
    onPaste?.(event);
  }, [onPaste]);
  rows = multiline === true ? rows : undefined;
  return /*#__PURE__*/_react.default.createElement("div", {
    className: "formInputWrapper"
  }, /*#__PURE__*/_react.default.createElement(Component, {
    id: controlId,
    type: inputType,
    name: controlId,
    className: (0, _arkReact.concatClassName)('formControl formInput', isInvalid ? 'formControl-invalid' : null, readOnly ? 'formControl-readOnly' : null, className),
    placeholder: placeholder,
    autoComplete: autocomplete,
    value: internalValue ?? '',
    spellCheck: spellcheck,
    autoCapitalize: autocapitalize,
    rows: rows,
    readOnly: readOnly,
    min: min,
    max: max,
    inputMode: inputMode,
    pattern: pattern,
    onChange: onInputChange,
    onFocus: handleInputFocus,
    onBlur: handleInputBlur,
    onPaste: handleInputPaste
  }), icon != null && /*#__PURE__*/_react.default.createElement("div", {
    className: "formInputIcon"
  }, icon), showLengthIndicator && /*#__PURE__*/_react.default.createElement("span", {
    className: "formInputLength"
  }, textLength, "/", maxlength));
};
process.env.NODE_ENV !== "production" ? FormInput.propTypes = {
  type: _propTypes.default.oneOf(['text', 'password', 'number']),
  value: _propTypes.default.string,
  className: _propTypes.default.string,
  placeholder: _propTypes.default.string,
  autocomplete: _propTypes.default.string,
  spellcheck: _propTypes.default.bool,
  autocapitalize: _propTypes.default.oneOf(['none', 'sentences', 'words', 'characters']),
  forceCase: _propTypes.default.oneOf(availableCase),
  multiline: _propTypes.default.bool,
  rows: _propTypes.default.number,
  maxlength: _propTypes.default.number,
  readOnly: _propTypes.default.bool,
  icon: _propTypes.default.node,
  required: _propTypes.default.bool,
  min: _propTypes.default.number,
  max: _propTypes.default.number,
  regex: _propTypes.default.string,
  managed: _propTypes.default.bool,
  onChange: _propTypes.default.func,
  onFocus: _propTypes.default.func,
  onBlur: _propTypes.default.func,
  state: _propTypes.default.oneOf(['error', 'warning', 'ok'])
} : void 0;
FormInput.defaultProps = {
  type: undefined,
  value: undefined,
  className: undefined,
  placeholder: undefined,
  autocomplete: 'off',
  spellcheck: undefined,
  autocapitalize: undefined,
  forceCase: undefined,
  multiline: false,
  rows: 1,
  maxlength: undefined,
  readOnly: undefined,
  icon: undefined,
  required: false,
  min: undefined,
  max: undefined,
  regex: undefined,
  managed: false,
  onChange: undefined,
  onFocus: undefined,
  onBlur: undefined,
  state: 'ok'
};
var _default = exports.default = FormInput;